import React from "react";
import {
  CalendarOutlined,
  CheckCircleOutlined,
  DollarOutlined,
  SendOutlined,
} from "@ant-design/icons";
import { Typography } from "antd";
import { useTranslation } from "react-i18next";
import { colors, fontSize } from "~/styles/tokens";
import { JourneyWhyExpander } from "./JourneyWhyExpander";

const { Title, Paragraph } = Typography;

const MONEY_STEPS = [
  {
    key: "month",
    icon: <CalendarOutlined />,
    labelKey: "JourneyGuide_Money_Step_Month" as const,
    bodyKey: "JourneyGuide_Money_Step_Month_Body" as const,
    color: colors.actionPrimary,
  },
  {
    key: "due",
    icon: <DollarOutlined />,
    labelKey: "JourneyGuide_Money_Step_Due" as const,
    bodyKey: "JourneyGuide_Money_Step_Due_Body" as const,
    color: colors.statusWarning,
  },
  {
    key: "request",
    icon: <SendOutlined />,
    labelKey: "JourneyGuide_Money_Step_Request" as const,
    bodyKey: "JourneyGuide_Money_Step_Request_Body" as const,
    color: colors.actionTertiary,
  },
  {
    key: "approve",
    icon: <CheckCircleOutlined />,
    labelKey: "JourneyGuide_Money_Step_Approve" as const,
    bodyKey: "JourneyGuide_Money_Step_Approve_Body" as const,
    color: colors.statusSuccess,
  },
];

interface MoneyJourneySectionProps {
  isMobile: boolean;
}

/**
 * Money — rent collection sequence through payment approval.
 */
export const MoneyJourneySection: React.FC<MoneyJourneySectionProps> = ({
  isMobile,
}) => {
  const { t } = useTranslation();

  return (
    <div className={`journey-content${isMobile ? " is-mobile" : ""}`}>
      <Title level={isMobile ? 3 : 2} style={{ marginTop: 0 }}>
        {t("JourneyGuide_Money_Title")}
      </Title>
      <Paragraph
        className="journey-lead"
        style={{ fontSize: isMobile ? fontSize.body : fontSize.emphasis }}
      >
        {t("JourneyGuide_Money_Description")}
      </Paragraph>

      <ol className={`journey-sequence${isMobile ? " is-stacked" : ""}`}>
        {MONEY_STEPS.map((step, index) => (
          <li
            key={step.key}
            className="journey-sequence-step"
            style={{ ["--seq-color" as string]: step.color }}
          >
            {!isMobile && index > 0 && (
              <span className="journey-sequence-connector" aria-hidden />
            )}
            <span className="journey-sequence-icon" aria-hidden>
              {step.icon}
            </span>
            <div className="journey-sequence-copy">
              <div className="journey-sequence-label">{t(step.labelKey)}</div>
              <p className="journey-sequence-body">{t(step.bodyKey)}</p>
            </div>
          </li>
        ))}
      </ol>

      <JourneyWhyExpander
        summaryKey="JourneyGuide_Money_Why_Summary"
        detailKey="JourneyGuide_Money_Why_Detail"
      />
    </div>
  );
};
